import type { Toast, ToastOptions } from '@helpers/types/Toast';
import { generateUUID } from '@therame/shared';
import { defineStore } from 'pinia';
import { computed, ref } from 'vue';

const TOAST_AUTO_DISMISS_MS = 4000;
/** Actionable toasts get a longer window so the user has time to act. */
const TOAST_ACTIONABLE_DISMISS_MS = 8000;
/**
 * One toast at a time: a new one takes the slot and whatever stood there goes.
 * Fields that save on their own can fire in quick succession, and a stack of
 * near-identical confirmations reads as noise — the newest is the one that still
 * has an action attached to it.
 */
const MAX_VISIBLE_TOASTS = 1;

export const useToastStore = defineStore('toast', () => {
  /**
   * toast stack of all currently visible toasts
   */
  const toasts = ref<Toast[]>([]);

  const dismissToast = (id: string) => {
    const index = toasts.value.findIndex((toast) => toast.id === id);
    if (index >= 0) {
      toasts.value.splice(index, 1);
    }
  };

  const addToast = (options: ToastOptions) => {
    const id = generateUUID();
    // All toasts auto-dismiss unless they opt out with `persist`. Actionable
    // toasts get a longer window so the user has time to act on them. The banner
    // runs the countdown (it pauses on hover) and closes back through dismissToast.
    const autoCloseMs = options.persist
      ? undefined
      : options.buttonAction
        ? TOAST_ACTIONABLE_DISMISS_MS
        : TOAST_AUTO_DISMISS_MS;
    toasts.value.push({ id, details: options, autoCloseMs });
    if (toasts.value.length > MAX_VISIBLE_TOASTS) {
      toasts.value.splice(0, toasts.value.length - MAX_VISIBLE_TOASTS);
    }
    return id;
  };

  return {
    toasts: computed(() => toasts.value),
    addToast,
    dismissToast,
  };
});
